import { TGetEnumOptionReqDto, TGetEnumOptionsResDto } from "@/api/services/global/enum/dtos/get-enum-options.dto";
import { CACHED_TIME } from "@/constants/common";
import { QUERY_KEYS } from "@/constants/queryKeys";
import { valueOf } from "@/types/common";
import { globalApi } from "@/utils/api";
import { createUrlWithParams } from "@/utils/url";
import { useSharedEnumOptions } from "@/features/sharing/context/shared-enum-context";
import { useQuery } from "@tanstack/react-query";
import { GENDER } from "@/types/enum";

export type TEnumName = valueOf<
  [
    "EInformationType",
    "EEmploymentStatus",
    "EDocumentStatus",
    "EQuarter",
    "EGender",
    "ERecruitmentStatus",
    "EEmailDelivery",
    "EMatchContractStatus",
    "EJobStatus",
    "EJobTag",
    "EClientType",
    "EEmploymentType",
    "ESalaryType",
    "EYesNoPendingStatus",
    "EJapaneseLevelType",
    "ERecruitmentLocationType",
    "EForeignLanguage",
    "EEducationLevel",
    "EPartnerContactMethod",
    "EJobCaseFilter",
    "ECaseStatus",
    "EJobResidenceStatus",
    "ETalentContactType",
    "EInterviewMethod",
    "EMeetingToolType",
  ]
>;

export const fetchEnumApi = async (enumName: TEnumName, req?: TGetEnumOptionReqDto): Promise<TGetEnumOptionsResDto> => {
  const url = createUrlWithParams(`/enums/${enumName}/options`, req);
  const rs = await globalApi.get<TGetEnumOptionsResDto>(url);
  return rs.data;
};

export const useEnum = (enumName?: TEnumName, req?: TGetEnumOptionReqDto) => {
  // On shared (no-auth) pages, options come from the verify response via context — skip the auth-protected API.
  const sharedEnumOptions = useSharedEnumOptions();
  const sharedOptions = enumName ? sharedEnumOptions?.[enumName as string] : undefined;

  const { data, ...rest } = useQuery({
    queryKey: [QUERY_KEYS.GLOBAL.FETCH_ENUM, enumName, req?.lang],
    queryFn: () => (enumName ? fetchEnumApi(enumName, req) : undefined),
    enabled: Boolean(enumName) && !sharedOptions,
    staleTime: CACHED_TIME,
    gcTime: CACHED_TIME,
  });

  const rawOptions = sharedOptions ?? data?.data ?? [];
  const options = enumName === "EGender" ? rawOptions.filter((opt) => String(opt.value) !== String(GENDER.OTHER)) : rawOptions;

  return { data: options, ...rest };
};
